/**
 * Widget booking flow state machine (useReducer-based, no xstate).
 * Steps: service → staff → datetime → details → confirmation
 */

import type { Service, Staff, TimeSlot } from '@api/types';

export type WidgetStep = 'service' | 'staff' | 'datetime' | 'details' | 'confirmation';

export interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
  notes?: string;
}

export interface WidgetState {
  step: WidgetStep;
  selectedService: Service | null;
  selectedStaff: Staff | null;
  skipStaffStep: boolean;
  selectedSlot: TimeSlot | null;
  selectedDate: string | null;
  customerDetails: CustomerDetails | null;
  confirmationId: string | null;
  confirmationTrackingToken: string | null;
  /** ISO timestamp by which the booking will be auto-cancelled if the customer doesn't confirm. */
  confirmationDeadline: string | null;
  error: string | null;
}

export type WidgetAction =
  | { type: 'SELECT_SERVICE'; service: Service }
  | { type: 'SELECT_STAFF'; staff: Staff | null }
  | { type: 'SKIP_STAFF' }
  | { type: 'SELECT_DATE'; date: string }
  | { type: 'SELECT_SLOT'; slot: TimeSlot }
  | { type: 'SUBMIT_DETAILS'; details: CustomerDetails }
  | {
      type: 'BOOKING_CONFIRMED';
      id: string;
      trackingToken: string;
      confirmationDeadline?: string | null;
    }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'GO_BACK' }
  | { type: 'RESET' };

const STEP_ORDER: WidgetStep[] = ['service', 'staff', 'datetime', 'details', 'confirmation'];

export const initialWidgetState: WidgetState = {
  step: 'service',
  selectedService: null,
  selectedStaff: null,
  skipStaffStep: false,
  selectedSlot: null,
  selectedDate: null,
  customerDetails: null,
  confirmationId: null,
  confirmationTrackingToken: null,
  confirmationDeadline: null,
  error: null,
};

export function widgetReducer(state: WidgetState, action: WidgetAction): WidgetState {
  switch (action.type) {
    case 'SELECT_SERVICE':
      return {
        ...state,
        selectedService: action.service,
        step: 'staff',
        error: null,
      };

    case 'SELECT_STAFF':
      return {
        ...state,
        selectedStaff: action.staff,
        skipStaffStep: false,
        step: 'datetime',
        error: null,
      };

    case 'SKIP_STAFF':
      return {
        ...state,
        selectedStaff: null,
        skipStaffStep: true,
        step: 'datetime',
        error: null,
      };

    case 'SELECT_DATE':
      return {
        ...state,
        selectedDate: action.date,
        selectedSlot: null,
        error: null,
      };

    case 'SELECT_SLOT':
      return {
        ...state,
        selectedSlot: action.slot,
        step: 'details',
        error: null,
      };

    case 'SUBMIT_DETAILS':
      return {
        ...state,
        customerDetails: action.details,
        error: null,
      };

    case 'BOOKING_CONFIRMED':
      return {
        ...state,
        step: 'confirmation',
        confirmationId: action.id,
        confirmationTrackingToken: action.trackingToken,
        confirmationDeadline: action.confirmationDeadline ?? null,
        error: null,
      };

    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'GO_BACK': {
      const currentIndex = STEP_ORDER.indexOf(state.step);
      if (currentIndex <= 0) return state;
      let prevStep = STEP_ORDER[currentIndex - 1];
      // Skip staff step when going back if we skipped it going forward
      if (prevStep === 'staff' && state.skipStaffStep) {
        prevStep = 'service';
      }
      return { ...state, step: prevStep, error: null };
    }

    case 'RESET':
      return initialWidgetState;

    default:
      return state;
  }
}
